import { useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2, X } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { resolveCitationPage } from "../lib/document-open.js";
import {
  hasOpenableCitationSource,
  openCitationDocument,
  type OnOpenCitationDocument,
} from "../lib/open-citation-document.js";
import type { RagCitation } from "../types.js";

function formatCitationSubtitle(
  citation: Pick<RagCitation, "page" | "section" | "versionLabel">,
  resolvedPage?: number,
): string | null {
  const parts: string[] = [];

  if (resolvedPage != null) {
    parts.push(`Page ${resolvedPage}`);
  } else if (citation.page) {
    parts.push(`Page ${citation.page}`);
  }

  if (citation.section) {
    parts.push(citation.section);
  }

  if (citation.versionLabel) {
    parts.push(`Version ${citation.versionLabel}`);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
}

interface CitationPanelProps {
  citation: RagCitation;
  threadId?: string;
  onClose: () => void;
  onOpenDocument?: OnOpenCitationDocument;
}

export function CitationPanel({
  citation,
  threadId,
  onClose,
  onOpenDocument,
}: CitationPanelProps) {
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [isExcerptExpanded, setIsExcerptExpanded] = useState(false);
  const [canToggleExcerpt, setCanToggleExcerpt] = useState(false);
  const excerptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsExcerptExpanded(false);
    setCanToggleExcerpt(false);
  }, [citation.id]);

  useEffect(() => {
    if (isExcerptExpanded) {
      return;
    }

    const element = excerptRef.current;
    if (!element) {
      return;
    }

    const checkOverflow = () => {
      if (element.scrollHeight > element.clientHeight + 1) {
        setCanToggleExcerpt(true);
      }
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [citation.excerpt, citation.id, isExcerptExpanded]);

  const excerptParts = citation.highlight
    ? citation.excerpt.split(citation.highlight)
    : [citation.excerpt];
  const resolvedPage = resolveCitationPage(citation);
  const subtitle = formatCitationSubtitle(citation, resolvedPage);
  const canOpen = hasOpenableCitationSource(citation);

  async function handleOpenDocument() {
    setOpening(true);
    setOpenError(null);

    try {
      await openCitationDocument({ citation, threadId, onOpenDocument });
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : "Failed to open document");
    } finally {
      setOpening(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        aria-label="Close citation panel"
        onClick={onClose}
      />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-[min(100vw,380px)] shrink-0 flex-col overflow-hidden border-l border-[var(--lm-border-subtle)] bg-white lg:static lg:z-auto lg:w-[380px] dark:border-white/10 dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900">
      <div className="flex items-start justify-between border-b border-[var(--lm-border-subtle)] p-4 dark:border-white/10">
        <div>
          <div className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-white">{citation.documentTitle}</div>
          {subtitle || citation.versionLabel ? (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[var(--lm-muted)] dark:text-slate-400">
              {subtitle ? <span>{subtitle}</span> : null}
              {citation.versionLabel ? (
                <span className="rounded bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--lm-muted)] dark:bg-slate-700/80 dark:text-slate-300">
                  v {citation.versionLabel}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <IconButton type="button" size="sm" title="Close" onClick={onClose} className="text-slate-400">
          <X className="h-4 w-4" />
        </IconButton>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] text-slate-400">
          <span>Relevance</span>
          <div className="h-1 flex-1 rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-violet-600"
              style={{ width: `${citation.relevance}%` }}
            />
          </div>
          <span>{citation.relevance}%</span>
        </div>
        <div className="mb-4">
          <div
            ref={excerptRef}
            className={cn(
              "relative rounded-xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] p-4 text-xs leading-relaxed text-[var(--lm-foreground)] dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-300",
              !isExcerptExpanded && "max-h-[180px] overflow-hidden",
            )}
          >
            {excerptParts.map((part, i) => (
              <span key={i}>
                {part}
                {i < excerptParts.length - 1 && citation.highlight ? (
                  <mark className="rounded bg-violet-500/20 px-0.5 text-[#6d28d9] dark:bg-violet-500/35 dark:text-white">
                    {citation.highlight}
                  </mark>
                ) : null}
              </span>
            ))}
            {!isExcerptExpanded && canToggleExcerpt ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[var(--surface-sunken)] to-transparent dark:from-slate-950/90 dark:to-transparent" />
            ) : null}
          </div>
          {canToggleExcerpt ? (
            <button
              type="button"
              onClick={() => setIsExcerptExpanded((prev) => !prev)}
              aria-expanded={isExcerptExpanded}
              className="mt-1.5 text-[11px] text-violet-500 hover:text-violet-400 dark:text-violet-300 dark:hover:text-violet-200"
            >
              {isExcerptExpanded ? "Show less" : "Show more"}
            </button>
          ) : null}
        </div>
        {citation.attributes?.length ? (
          <dl className="mb-4 space-y-2 rounded-xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] p-3 dark:border-white/10 dark:bg-slate-950/30">
            {citation.attributes.map((attribute) => (
              <div key={`${attribute.label}:${attribute.value}`} className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 text-[11px]">
                <dt className="font-medium text-slate-400">{attribute.label}</dt>
                <dd className="min-w-0 overflow-hidden break-all text-slate-300">
                  {attribute.href ? (
                    <a
                      href={attribute.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block max-w-full break-all text-violet-500 underline underline-offset-2 hover:text-violet-400 dark:text-violet-300"
                    >
                      {attribute.value}
                    </a>
                  ) : (
                    attribute.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        <Button
          variant="outline"
          className="w-full"
          disabled={opening || !canOpen}
          onClick={() => void handleOpenDocument()}
        >
          {opening ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ExternalLink className="h-4 w-4" />
          )}
          Open full document
        </Button>
        {openError ? (
          <p className="mt-2 text-center text-[11px] text-red-400">{openError}</p>
        ) : null}
        {citation.chunkLabel ? (
          <p className="mt-3 text-center text-[10px] text-slate-500">{citation.chunkLabel}</p>
        ) : null}
      </div>
    </aside>
    </>
  );
}

export {
  buildDocumentOpenUrl,
  parsePageNumber,
  resolveCitationPage,
  isPdfOpenable,
} from "../lib/document-open.js";
